/**
 * The built-in straight stair (defs/stair.ts) as a data spec.
 *
 * How it works:
 *  - Outline: the footprint rectangle, stretched with the object.
 *  - Treads: ONE tread line drawn at the end of one step cell, repeated along
 *    the run (a linear repeat). The cell size follows the `step` param and
 *    the cells stretch evenly to fill the run (`fit: 'fill'`), so a resize
 *    changes HOW MANY treads there are, not their size. The last cell's line
 *    is dropped (`skip: 'last'`) — it would lie on the outline's end.
 *    Because both the pitch (`step`) and the count (`number of steps`) are
 *    bound to params, they are kept in sync automatically: resizing or
 *    editing the step recounts the steps (the typed step is kept), editing
 *    the number of steps divides the run evenly (step := run / count).
 *  - Walk line: an arrow along the centerline from a cross tick (bottom of
 *    the stair) to a chevron head (top), inset 15 % from both ends. Its
 *    points are formulas of the live size, so the group uses `fixed` resize
 *    (formulas already give the live coordinates). `reverse direction` flips
 *    it through the `dir` formula (+1 or −1).
 */

import { OBJECT_DEFINITION_SCHEMA_VERSION, type ObjectDefinitionSpec } from '../spec';

const W = 2.8;
const D = 0.9;
const HW = W / 2;
const HD = D / 2;
const STEP = 0.25;
/** Walk line ends and arrow size at the default size (the authored drawing). */
const TIP = HW * 0.85;
const HEAD = Math.min(D * 0.25, 0.15);

export const stairSpec: ObjectDefinitionSpec = {
  schemaVersion: OBJECT_DEFINITION_SCHEMA_VERSION,
  anchor: 'LINE',
  constraint: { kind: 'FREE' },
  defaultSize: { width: W, depth: D, height: 3 },
  params: [
    { key: 'treadDepth', kind: 'float', label: 'step', default: STEP, min: 0.15, max: 0.4, decimals: 2, distance: true, order: 11.4 },
    { key: 'stepCount', kind: 'int', label: 'number of steps', default: 11, min: 1, order: 11.5 },
    { key: 'reversed', kind: 'boolean', label: 'reverse direction', default: false },
  ],
  formulas: [
    { key: 'dir', label: 'Walk direction', expr: '1 - 2 * reversed', note: 'Walk direction along the width: +1 normally, -1 when reversed' },
    { key: 'tip', label: 'Walk line half length', expr: 'dir * width / 2 * 0.85', note: 'Top end of the walk line: 85 % of the half run, in the walk direction' },
    { key: 'headSize', label: 'Arrow size', expr: 'min(depth * 0.25, 0.15)', note: 'Start tick and arrow head size: a quarter of the depth, at most 15 cm' },
  ],
  shapes: [
    { id: 'outline', name: 'Outline', kind: 'rect', min: [-HW, -HD], max: [HW, HD] },
    // One step cell spans [-HW, -HW + STEP]; its tread line sits at the cell's end.
    { id: 'tread', name: 'Tread', kind: 'polyline', points: [[-HW + STEP, -HD], [-HW + STEP, HD]], closed: false },
    {
      id: 'walkLine',
      name: 'Walk line',
      kind: 'polyline',
      points: [[-TIP, 0], [TIP, 0]],
      closed: false,
      expr: { 'p0.u': '-tip', 'p1.u': 'tip' },
    },
    {
      id: 'startTick',
      name: 'Start tick',
      kind: 'polyline',
      points: [[-TIP, -HEAD], [-TIP, HEAD]],
      closed: false,
      expr: { 'p0.u': '-tip', 'p0.n': '-headSize', 'p1.u': '-tip', 'p1.n': 'headSize' },
    },
    {
      id: 'arrowHead',
      name: 'Arrow head',
      kind: 'polyline',
      points: [[TIP - HEAD, HEAD], [TIP, 0], [TIP - HEAD, -HEAD]],
      closed: false,
      expr: { 'p0.u': 'tip - dir * headSize', 'p0.n': 'headSize', 'p1.u': 'tip', 'p2.u': 'tip - dir * headSize', 'p2.n': '-headSize' },
    },
  ],
  groups: [
    { id: 'outline', name: 'Outline', shapeIds: ['outline'], resize: { u: 'stretch', n: 'stretch' } },
    {
      id: 'treads',
      name: 'Treads',
      shapeIds: ['tread'],
      resize: { u: 'stretch', n: 'stretch' },
      repeat: {
        kind: 'linear',
        axis: 'u',
        cell: [-HW, -HW + STEP],
        pitch: { param: 'treadDepth' },
        count: { param: 'stepCount' },
        fit: 'fill',
        cellResize: 'stretch',
        skip: 'last',
        minCount: 1,
      },
    },
    { id: 'walkLine', name: 'Walk line', shapeIds: ['walkLine', 'startTick', 'arrowHead'], resize: { u: 'fixed', n: 'fixed' } },
  ],
  model3d: null,
};
